import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import avatar from "@/images/avatar.webp"
import Navbar from "@/Components/Navbar"
import { getUserCreds, host } from "@/utils/utils"
import { SAFE_PAGE_FRAME } from "@/utils/safeArea"
import { useEffect, useState } from "react"
import { useAppSelector } from "@/store/hooks"
import OverviewTab from "./OverviewTab"
import EconomyTab from "./EconomyTab"
import BoardTab from "./BoardTab"
import HistoryTab from "./HistoryTab"
import LeaderboardTab from "./LeaderboardTab"
import {
  clampScore,
  formatDate,
  formatNumber,
  formatPercent,
  getNextTier,
  getRankTier,
} from "./statsUi"

type TabId = "overview" | "economy" | "board" | "history" | "leaderboard"

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Overview", icon: "fa-chart-simple" },
  { id: "economy", label: "Economy", icon: "fa-coins" },
  { id: "board", label: "Board Play", icon: "fa-chess-board" },
  { id: "history", label: "Recent Games", icon: "fa-clock-rotate-left" },
  { id: "leaderboard", label: "Leaderboard", icon: "fa-ranking-star" },
]

const Stats = () => {
  const user = useAppSelector((state) => state.global.userData)
  const [stats, setStats] = useState<IUserLifetimeStatsResponse | null>(null)
  const [leaderboard, setLeaderboard] = useState<ILeaderboardEntry[]>([])
  const [loadingStats, setLoadingStats] = useState(true)
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>("overview")

  const email = getUserCreds().email

  useEffect(() => {
    let cancelled = false

    const loadStats = async () => {
      if (!email) {
        setLoadingStats(false)
        setError("Sign in to see your stats.")
        return
      }
      try {
        const response = await fetch(`${host}/api/stats/user`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        })
        if (!response.ok) throw new Error(`Request failed (${response.status})`)
        const data = await response.json()
        if (!cancelled) setStats(data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load stats")
        }
      } finally {
        if (!cancelled) setLoadingStats(false)
      }
    }

    const loadLeaderboard = async () => {
      try {
        const response = await fetch(`${host}/api/stats/leaderboard?limit=25`)
        if (!response.ok) throw new Error("Failed to load leaderboard")
        const data = await response.json()
        if (!cancelled) setLeaderboard(data.leaderboard || [])
      } catch {
        // The ladder is supporting detail — a failure here leaves the rest of
        // the page perfectly usable, so it stays a silent empty state.
        if (!cancelled) setLeaderboard([])
      } finally {
        if (!cancelled) setLoadingLeaderboard(false)
      }
    }

    loadStats()
    loadLeaderboard()
    return () => {
      cancelled = true
    }
  }, [email])

  const rating = stats?.rating ?? 0
  const tier = getRankTier(rating)
  const nextTier = getNextTier(rating)
  // How far through the current band the rating sits. The top band has no next
  // tier to climb to, so it simply reads as full.
  const tierProgress = nextTier
    ? clampScore(((rating - tier.min) / (nextTier.min - tier.min)) * 100)
    : 100

  const renderBody = () => {
    if (loadingStats) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-white/50">
            <i className="fas fa-circle-notch fa-spin text-3xl mobile:text-[22px]/[1.15] text-amber-400" />
            <p className="font-fourth mobile:text-[14px]/[1.4] tracking-wide">Crunching your numbers…</p>
          </div>
        </div>
      )
    }
    if (error || !stats) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-6 py-5 text-center">
            <i className="fas fa-triangle-exclamation mb-2 text-2xl mobile:text-[20px]/[1.15] text-rose-300" />
            <p className="font-fourth text-white">Could not load your stats</p>
            <p className="mt-1 text-sm mobile:text-[12px]/[1.4] text-white/50">{error}</p>
          </div>
        </div>
      )
    }

    switch (activeTab) {
      case "economy":
        return <EconomyTab stats={stats} />
      case "board":
        return <BoardTab stats={stats} />
      case "history":
        return <HistoryTab stats={stats} />
      case "leaderboard":
        return (
          <LeaderboardTab
            leaderboard={leaderboard}
            loading={loadingLeaderboard}
            myEmail={email}
            myRating={rating}
          />
        )
      default:
        return <OverviewTab stats={stats} />
    }
  }

  return (
    <div
      className="relative w-screen h-screen bg-cover flex flex-col"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      <Navbar />

      <div className={`relative z-10 flex-1 min-h-0 overflow-y-auto ${SAFE_PAGE_FRAME}`}>
        <div className="mx-auto flex w-full max-w-[110rem] flex-col gap-4 sm:gap-2.5">
          {/* Hero */}
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-black/50 p-6 sm:p-4 mobile:p-[14px]"
            style={{ boxShadow: `inset 0 0 120px -40px ${tier.glow}` }}
          >
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl"
              style={{ backgroundColor: tier.glow }}
            />
            <div className="relative flex flex-wrap items-center justify-between gap-6 sm:gap-3">
              <div className="flex items-center gap-5 sm:gap-3">
                <div
                  className="relative h-24 w-24 sm:h-16 sm:w-16 mobile:h-[56px] mobile:w-[56px] shrink-0 rounded-full border-2 p-1"
                  style={{ borderColor: tier.color }}
                >
                  <img
                    src={user?.profileImage || avatar}
                    alt={user?.username || "Player"}
                    className="h-full w-full rounded-full object-cover"
                  />
                  <span
                    className="absolute -bottom-1 left-1/2 flex h-7 w-7 sm:h-5 sm:w-5 mobile:h-[20px] mobile:w-[20px] -translate-x-1/2 items-center justify-center rounded-full border-2 border-black/70"
                    style={{ backgroundColor: tier.color }}
                  >
                    <i
                      className={`fas ${tier.icon} text-xs sm:text-[0.6rem] mobile:text-[10px]/[1.4] text-black/80`}
                    />
                  </span>
                </div>

                <div className="min-w-0">
                  <p className="text-xs mobile:text-[11px]/[1.4] font-semibold uppercase tracking-wider text-amber-400/70">
                    Career Record
                  </p>
                  <h1 className="font-fourth text-3xl sm:text-xl mobile:text-[22px]/[1.15] font-bold tracking-wide text-white">
                    {user?.username || stats?.username || "Player"}
                  </h1>
                  <p
                    className="mt-0.5 font-fourth mobile:text-[13px]/[1.4] font-bold tracking-wide"
                    style={{ color: tier.color }}
                  >
                    <i className={`fas ${tier.icon} mr-2 text-sm mobile:text-[12px]/[1.4]`} />
                    {tier.name}
                  </p>
                  {stats && (
                    <p className="mt-1 text-xs mobile:text-[11px]/[1.4] text-white/40">
                      First game {formatDate(stats.firstPlayedAt)} · Last played{" "}
                      {formatDate(stats.lastPlayedAt)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 sm:gap-2">
                <div className="rounded-2xl border border-white/10 bg-black/40 px-5 py-3 sm:px-3 sm:py-2 mobile:px-[12px] mobile:py-[8px] text-center">
                  <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                    Rating
                  </p>
                  <p
                    className="font-fourth text-3xl sm:text-xl mobile:text-[22px]/[1] font-bold leading-none"
                    style={{ color: tier.color }}
                  >
                    {formatNumber(rating)}
                  </p>
                  <p className="mt-1 text-[0.65rem] mobile:text-[10px]/[1.4] text-white/40">
                    peak {formatNumber(stats?.peakRating ?? 0)}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/40 px-5 py-3 sm:px-3 sm:py-2 mobile:px-[12px] mobile:py-[8px] text-center">
                  <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                    Games
                  </p>
                  <p className="font-fourth text-3xl sm:text-xl mobile:text-[22px]/[1] font-bold leading-none text-white">
                    {formatNumber(stats?.gamesPlayed ?? 0)}
                  </p>
                  <p className="mt-1 text-[0.65rem] mobile:text-[10px]/[1.4] text-white/40">
                    {formatNumber(stats?.gamesWon ?? 0)} won
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/40 px-5 py-3 sm:px-3 sm:py-2 mobile:px-[12px] mobile:py-[8px] text-center">
                  <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                    Win Rate
                  </p>
                  <p className="font-fourth text-3xl sm:text-xl mobile:text-[22px]/[1] font-bold leading-none text-emerald-300">
                    {formatPercent(stats?.derived?.winRate ?? 0, 0)}
                  </p>
                  <p className="mt-1 text-[0.65rem] mobile:text-[10px]/[1.4] text-white/40">
                    {formatPercent(stats?.derived?.podiumRate ?? 0, 0)} top half
                  </p>
                </div>
              </div>
            </div>

            {/* Tier progress */}
            <div className="relative mt-5 sm:mt-3">
              <div className="mb-1.5 flex items-center justify-between text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider">
                <span style={{ color: tier.color }}>{tier.name}</span>
                <span className="text-white/40">
                  {nextTier
                    ? `${formatNumber(nextTier.min - rating)} to ${nextTier.name}`
                    : "Top tier reached"}
                </span>
              </div>
              <div className="h-2 mobile:h-[7px] w-full overflow-hidden rounded-full bg-white/5">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${tierProgress}%`,
                    background: `linear-gradient(90deg, ${tier.color}88, ${tier.color})`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-black/40 p-1.5">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 rounded-xl px-4 py-2.5 sm:px-3 sm:py-1.5 mobile:px-[12px] mobile:py-[7px] text-sm sm:text-xs mobile:text-[12px]/[1.4] font-fourth font-bold tracking-wide transition-all ${
                    isActive
                      ? "bg-amber-500/20 text-amber-200 shadow-lg"
                      : "text-white/50 hover:bg-white/5 hover:text-white/80"
                  }`}
                >
                  <i className={`fas ${tab.icon} text-xs mobile:text-[11px]/[1.4]`} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {renderBody()}
        </div>
      </div>
    </div>
  )
}

export default Stats
